import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Tooltip,
  Popover,
  Alert,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Lock,
  WarningAmber,
  Add,
  CalendarMonth,
  CheckCircle,
  EventBusy,
} from "@mui/icons-material";
import api from "../../services/api";

// ── Constantes ───────────────────────────────────────────────────────────────
const DIAS_SEMANA = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MESES_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// Colores por cantidad de eventos en el día
const getDayPalette = (count, isPast) => {
  if (isPast)   return { bg: "rgba(255,255,255,0.02)", border: "transparent",            dot: null,      text: "rgba(255,255,255,0.18)" };
  if (count === 0) return { bg: "rgba(34,197,94,0.06)",  border: "rgba(34,197,94,0.18)",   dot: null,      text: "rgba(255,255,255,0.75)" };
  if (count === 1) return { bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.40)",  dot: "#f59e0b", text: "#fbbf24" };
  if (count === 2) return { bg: "rgba(249,115,22,0.10)", border: "rgba(249,115,22,0.40)",  dot: "#f97316", text: "#fb923c" };
  return             { bg: "rgba(239,68,68,0.10)",   border: "rgba(239,68,68,0.40)",    dot: "#ef4444", text: "#f87171" };
};

const estadoColor = {
  Confirmado: "#22c55e",
  Pendiente:  "#f59e0b",
  "En curso": "#8b5cf6",
  Completado: "#6366f1",
  Presupuesto:"#94a3b8",
};

// ── Componente principal ──────────────────────────────────────────────────────
/**
 * CalendarDisponibilidad
 * Muestra un calendario mensual con disponibilidad de fechas para eventos.
 *
 * Props:
 *  - userRole: "admin" | "colaborador"
 *  - onCreateEvent: (fechaStr: string) => void   (null = no crear)
 */
const CalendarDisponibilidad = ({ userRole = "admin", onCreateEvent }) => {
  const today  = new Date();
  const [mes,  setMes]  = useState(today.getMonth());
  const [anio, setAnio] = useState(today.getFullYear());

  const [disponibilidad, setDisponibilidad] = useState({}); // { "YYYY-MM-DD": { count, eventos } }
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  // Popover
  const [anchorEl,    setAnchorEl]    = useState(null);
  const [selectedDay, setSelectedDay] = useState(null); // { dateStr, count, eventos, isPast, isToday }

  // Diálogo de confirmación (admin: crear en día con eventos)
  const [confirmOpen, setConfirmOpen] = useState(false);

  const todayStr = today.toLocaleDateString("en-CA"); // YYYY-MM-DD (CDMX safe)

  // ── Fetch disponibilidad al cambiar mes/año ────────────────────────────────
  useEffect(() => {
    const mesStr = `${anio}-${String(mes + 1).padStart(2, "0")}`;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/disponibilidad?mes=${mesStr}`);
        if (cancelled) return;
        const fechas = res.data?.fechas ?? [];
        const map = {};
        fechas.forEach((f) => { map[f.fecha] = f; });
        setDisponibilidad(map);
      } catch (err) {
        if (!cancelled && err.response?.status !== 401) {
          setError("No se pudo cargar la disponibilidad. Intenta de nuevo.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [mes, anio]);

  // ── Construcción de celdas del calendario ─────────────────────────────────
  const calendarCells = useMemo(() => {
    const firstWeekday = new Date(anio, mes, 1).getDay(); // 0=Dom
    const daysInMonth  = new Date(anio, mes + 1, 0).getDate();
    const cells = Array(firstWeekday).fill(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [mes, anio]);

  const getDateStr = (day) =>
    `${anio}-${String(mes + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

  // ── Navegación ─────────────────────────────────────────────────────────────
  const goBack = () => {
    if (mes === 0) { setMes(11); setAnio((y) => y - 1); }
    else setMes((m) => m - 1);
  };
  const goNext = () => {
    if (mes === 11) { setMes(0); setAnio((y) => y + 1); }
    else setMes((m) => m + 1);
  };
  const goToday = () => { setMes(today.getMonth()); setAnio(today.getFullYear()); };

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleCellClick = (event, day) => {
    if (!day) return;
    const dateStr = getDateStr(day);
    const isPast  = dateStr < todayStr;
    if (isPast) return;
    const info    = disponibilidad[dateStr];
    const count   = info?.count ?? 0;
    setSelectedDay({ dateStr, count, eventos: info?.eventos ?? [], isPast, isToday: dateStr === todayStr });
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => { setAnchorEl(null); setSelectedDay(null); };

  const handleCreate = () => {
    if (onCreateEvent && selectedDay) onCreateEvent(selectedDay.dateStr);
    setConfirmOpen(false);
    closePopover();
  };

  // Crear en día ocupado (admin): abrir confirmación primero
  const handleCreateRequest = () => {
    if (!selectedDay || !onCreateEvent) return;
    if (selectedDay.count > 0) {
      setConfirmOpen(true); // muestra diálogo de confirmación
    } else {
      handleCreate(); // día libre → crear directo
    }
  };

  // Resumen del mes
  const mesPrefix = `${anio}-${String(mes + 1).padStart(2, "0")}`;
  const diasOcupados = Object.keys(disponibilidad).filter((d) => d.startsWith(mesPrefix)).length;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Cabecera de navegación ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <IconButton onClick={goBack} size="small"
          sx={{ color: "text.secondary", border: "1px solid rgba(255,255,255,0.10)", borderRadius: "10px", p: 0.8 }}>
          <ChevronLeft />
        </IconButton>

        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: "#e2e8f0", lineHeight: 1.2 }}>
            {MESES_ES[mes]} {anio}
          </Typography>
          {loading && <CircularProgress size={11} sx={{ mt: 0.5 }} />}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="Ir a hoy">
            <IconButton onClick={goToday} size="small"
              sx={{ color: "#6366f1", border: "1px solid rgba(99,102,241,0.3)", borderRadius: "10px", p: 0.8, fontSize: "0.62rem", fontWeight: 700 }}>
              <CalendarMonth sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <IconButton onClick={goNext} size="small"
            sx={{ color: "text.secondary", border: "1px solid rgba(255,255,255,0.10)", borderRadius: "10px", p: 0.8 }}>
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }} onClose={() => setError("")}>{error}</Alert>}

      {/* ── Leyenda ── */}
      <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2 }, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        {[
          { color: "rgba(34,197,94,0.7)",  label: "Libre" },
          { color: "#f59e0b",              label: "1 evento" },
          { color: "#f97316",              label: "2 eventos" },
          { color: "#ef4444",              label: "3+ eventos" },
        ].map((l) => (
          <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: l.color, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.67rem" }}>{l.label}</Typography>
          </Box>
        ))}
        {userRole === "admin" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, ml: "auto" }}>
            <WarningAmber sx={{ fontSize: 12, color: "#ef4444" }} />
            <Typography variant="caption" sx={{ color: "#f87171", fontSize: "0.67rem" }}>= Requiere aprobación admin</Typography>
          </Box>
        )}
        {userRole === "colaborador" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, ml: "auto" }}>
            <Lock sx={{ fontSize: 11, color: "#94a3b8" }} />
            <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.67rem" }}>= Solo consulta</Typography>
          </Box>
        )}
      </Box>

      {/* ── Encabezado días de la semana ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.4, mb: 0.4 }}>
        {DIAS_SEMANA.map((d) => (
          <Box key={d} sx={{ textAlign: "center", py: 0.4 }}>
            <Typography variant="caption"
              sx={{ color: "text.disabled", fontWeight: 700, fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: 0.5 }}>
              {d}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── Grilla del calendario ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.4 }}>
        {calendarCells.map((day, idx) => {
          if (!day) return <Box key={`pad-${idx}`} />;

          const dateStr  = getDateStr(day);
          const isPast   = dateStr < todayStr;
          const isToday  = dateStr === todayStr;
          const info     = disponibilidad[dateStr];
          const count    = info?.count ?? 0;
          const palette  = getDayPalette(count, isPast);
          const needsApproval = userRole === "admin" && count >= 2 && !isPast;
          const isLocked      = userRole === "colaborador" && count > 0 && !isPast;

          return (
            <Tooltip
              key={day}
              title={
                isPast ? "" :
                count > 0
                  ? `${count} evento${count > 1 ? "s" : ""}${isLocked ? " · Solo consulta" : ""}`
                  : "Día libre"
              }
              placement="top"
              arrow
            >
              <Box
                onClick={(e) => handleCellClick(e, day)}
                sx={{
                  aspectRatio: "1",
                  borderRadius: "11px",
                  border: `1.5px solid ${isToday ? "#6366f1" : palette.border}`,
                  bgcolor: palette.bg,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isPast ? "default" : "pointer",
                  position: "relative",
                  transition: "all 0.14s",
                  boxShadow: isToday ? "0 0 14px rgba(99,102,241,0.4)" : "none",
                  "&:hover": isPast ? {} : {
                    transform: "scale(1.1)",
                    zIndex: 2,
                    boxShadow: `0 4px 18px ${palette.dot ?? "rgba(99,102,241,0.3)"}50`,
                  },
                }}
              >
                <Typography sx={{
                  fontSize: { xs: "0.72rem", sm: "0.85rem" },
                  fontWeight: isToday ? 900 : 600,
                  color: palette.text,
                  lineHeight: 1,
                }}>
                  {day}
                </Typography>

                {/* Puntos indicadores de eventos */}
                {count > 0 && !isPast && (
                  <Box sx={{ display: "flex", gap: 0.25, mt: 0.3, justifyContent: "center" }}>
                    {Array.from({ length: Math.min(count, 3) }).map((_, i) => (
                      <Box key={i} sx={{ width: 3.5, height: 3.5, borderRadius: "50%", bgcolor: palette.dot }} />
                    ))}
                    {count > 3 && (
                      <Typography sx={{ fontSize: "0.48rem", color: palette.dot, lineHeight: 1, mt: 0.1 }}>+</Typography>
                    )}
                  </Box>
                )}

                {/* Badge: admin requiere aprobación */}
                {needsApproval && (
                  <Box sx={{
                    position: "absolute", top: 2, right: 2,
                    width: 7, height: 7, borderRadius: "50%",
                    bgcolor: "#ef4444", border: "1.5px solid #0a0f1e",
                  }} />
                )}

                {/* Ícono lock para colaborador en días ocupados */}
                {isLocked && (
                  <Lock sx={{
                    fontSize: 7, color: palette.dot, opacity: 0.9,
                    position: "absolute", bottom: 2, right: 2,
                  }} />
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* ── Resumen del mes ── */}
      <Box sx={{ display: "flex", gap: 1.5, mt: 2, flexWrap: "wrap" }}>
        {[
          { label: "Días ocupados",   value: diasOcupados,  color: "#f59e0b" },
          { label: "Con conflicto",   value: Object.values(disponibilidad).filter((d) => d.count >= 2).length, color: "#ef4444" },
        ].map((s) => (
          <Box key={s.label} sx={{
            display: "flex", alignItems: "center", gap: 0.8,
            px: 1.5, py: 0.6, borderRadius: "9px",
            bgcolor: s.value > 0 ? s.color + "12" : "rgba(255,255,255,0.03)",
            border: `1px solid ${s.value > 0 ? s.color + "30" : "rgba(255,255,255,0.06)"}`,
          }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: s.value > 0 ? s.color : "text.disabled", fontSize: "0.7rem" }}>
              {s.label}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 800, color: s.value > 0 ? s.color : "text.disabled", fontSize: "0.7rem" }}>
              {s.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── Popover: detalle del día ── */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            bgcolor: "#0f172a",
            border: "1px solid rgba(255,255,255,0.10)",
            borderRadius: "16px",
            p: 2,
            minWidth: 250,
            maxWidth: 340,
            boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          },
        }}
      >
        {selectedDay && (() => {
          const canCreate = userRole === "admin" || selectedDay.count === 0;
          const isLocked  = userRole === "colaborador" && selectedDay.count > 0;

          return (
            <Box>
              {/* Cabecera */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Typography sx={{ fontWeight: 800, color: "#f1f5f9", fontSize: "0.92rem" }}>
                  {new Date(selectedDay.dateStr + "T12:00:00").toLocaleDateString("es-MX", {
                    weekday: "long", day: "numeric", month: "long",
                  })}
                </Typography>
                {selectedDay.count === 0
                  ? <Chip icon={<CheckCircle sx={{ fontSize: "12px !important" }} />} label="Libre"
                      size="small" sx={{ height: 20, fontSize: "0.6rem", bgcolor: "rgba(34,197,94,0.15)", color: "#4ade80", "& .MuiChip-icon": { color: "#4ade80" } }} />
                  : <Chip icon={<EventBusy sx={{ fontSize: "12px !important" }} />}
                      label={`${selectedDay.count} evento${selectedDay.count > 1 ? "s" : ""}`}
                      size="small" sx={{ height: 20, fontSize: "0.6rem", bgcolor: "rgba(245,158,11,0.15)", color: "#fbbf24", "& .MuiChip-icon": { color: "#fbbf24" } }} />
                }
              </Box>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.07)", mb: 1.5 }} />

              {/* Lista de eventos del día */}
              {selectedDay.eventos.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, mb: 1.5, maxHeight: 220, overflowY: "auto",
                  "&::-webkit-scrollbar": { width: 4 },
                  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                  "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(255,255,255,0.15)", borderRadius: 2 },
                }}>
                  {selectedDay.eventos.map((ev, i) => (
                    <Box key={ev.id || i} sx={{
                      p: "9px 12px", borderRadius: "11px",
                      bgcolor: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.07)",
                    }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#e2e8f0", mb: 0.4 }} noWrap>
                        {ev.titulo}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
                        {ev.hora_inicio && (
                          <Chip label={`${ev.hora_inicio}${ev.hora_fin ? " – " + ev.hora_fin : ""}`} size="small"
                            sx={{ height: 16, fontSize: "0.58rem", bgcolor: "rgba(99,102,241,0.15)", color: "#a5b4fc", "& .MuiChip-label": { px: 0.7 } }} />
                        )}
                        <Chip label={ev.estado || "—"} size="small"
                          sx={{ height: 16, fontSize: "0.58rem",
                            bgcolor: (estadoColor[ev.estado] ?? "#94a3b8") + "22",
                            color: estadoColor[ev.estado] ?? "#94a3b8",
                            "& .MuiChip-label": { px: 0.7 } }} />
                        {ev.ubicacion && (
                          <Chip label={ev.ubicacion.length > 22 ? ev.ubicacion.slice(0, 22) + "…" : ev.ubicacion}
                            size="small" sx={{ height: 16, fontSize: "0.58rem", bgcolor: "rgba(255,255,255,0.05)", color: "text.disabled", "& .MuiChip-label": { px: 0.7 } }} />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: "text.disabled", textAlign: "center", py: 1.5, mb: 1 }}>
                  No hay eventos registrados en este día
                </Typography>
              )}

              {/* Admin: alerta de conflicto */}
              {userRole === "admin" && selectedDay.count >= 2 && (
                <Alert severity="warning" icon={<WarningAmber sx={{ fontSize: 16 }} />}
                  sx={{ mb: 1.5, borderRadius: "10px", py: 0.4, fontSize: "0.75rem",
                    bgcolor: "rgba(245,158,11,0.08)", color: "#fbbf24",
                    border: "1px solid rgba(245,158,11,0.28)",
                    "& .MuiAlert-icon": { color: "#f59e0b", alignItems: "center" },
                    "& .MuiAlert-message": { fontSize: "0.73rem" } }}>
                  Día con <strong>múltiples eventos</strong> — requiere aprobación del administrador
                </Alert>
              )}

              {/* Colaborador: aviso de solo consulta */}
              {isLocked && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, p: "9px 12px", borderRadius: "10px",
                  bgcolor: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)", mb: 1.5 }}>
                  <Lock sx={{ fontSize: 14, color: "#f87171", flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ color: "#f87171", fontWeight: 600 }}>
                    Día ocupado — solo puedes consultar la disponibilidad
                  </Typography>
                </Box>
              )}

              {/* Botón crear (admin siempre / colaborador solo si día libre) */}
              {canCreate && onCreateEvent && (
                <Button fullWidth variant="contained" size="small" startIcon={<Add />}
                  onClick={handleCreateRequest}
                  sx={{ borderRadius: "10px", bgcolor: "#6366f1", fontWeight: 700,
                    "&:hover": { bgcolor: "#4f46e5" }, py: 0.8 }}>
                  {selectedDay.count > 0 ? "Crear otro evento este día" : "Crear evento en esta fecha"}
                </Button>
              )}
            </Box>
          );
        })()}
      </Popover>
      {/* ── Diálogo de confirmación: crear en día con conflicto ── */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: "#0f172a",
            border: "1px solid rgba(245,158,11,0.35)",
            borderRadius: "18px",
            p: 0.5,
            maxWidth: 420,
            boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "12px",
              bgcolor: "rgba(245,158,11,0.15)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <WarningAmber sx={{ color: "#f59e0b", fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, color: "#f1f5f9", fontSize: "1rem", lineHeight: 1.2 }}>
                Día con eventos registrados
              </Typography>
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                {selectedDay && new Date(selectedDay.dateStr + "T12:00:00").toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.07)", mb: 2 }} />

          {/* Eventos existentes */}
          {selectedDay?.eventos?.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.7, mb: 2 }}>
              {selectedDay.eventos.map((ev, i) => (
                <Box key={ev.id || i} sx={{
                  display: "flex", alignItems: "center", gap: 1.2,
                  px: 1.5, py: 0.8, borderRadius: "10px",
                  bgcolor: "rgba(245,158,11,0.08)",
                  border: "1px solid rgba(245,158,11,0.20)",
                }}>
                  <EventBusy sx={{ fontSize: 15, color: "#f59e0b", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#fde68a", flex: 1 }} noWrap>
                    {ev.titulo || `Evento #${ev.id}`}
                  </Typography>
                  {ev.hora_inicio && (
                    <Typography sx={{ fontSize: "0.72rem", color: "#f59e0b", flexShrink: 0 }}>
                      {ev.hora_inicio}{ev.hora_fin ? ` – ${ev.hora_fin}` : ""}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          <Alert
            severity="warning"
            icon={false}
            sx={{
              borderRadius: "12px", bgcolor: "rgba(239,68,68,0.07)",
              border: "1px solid rgba(239,68,68,0.25)",
              "& .MuiAlert-message": { fontSize: "0.82rem", color: "#fca5a5" },
            }}
          >
            <strong>Valida la disponibilidad de servicios y productos</strong> antes de registrar un nuevo evento en esta fecha para evitar conflictos de cobertura que no se puedan cubrir.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "rgba(255,255,255,0.15)", color: "#94a3b8",
              borderRadius: "10px", fontWeight: 600, flex: 1,
              "&:hover": { borderColor: "rgba(255,255,255,0.3)", bgcolor: "rgba(255,255,255,0.05)" },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            sx={{
              bgcolor: "#f59e0b", color: "#0f172a", borderRadius: "10px",
              fontWeight: 800, flex: 1,
              "&:hover": { bgcolor: "#d97706" },
            }}
          >
            Continuar de todos modos
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarDisponibilidad;
